import { AlertCircle, BadgeCheck, ChevronDown, Loader, MessageCircleWarning, Sparkles } from "lucide-react";
import { type ReactNode, useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import {
  Calendar,
  type CxConfidence,
  ExternalLink,
  type ExtraordinaryCircumstance,
  formatPeriod,
  framingFor,
  MapPin,
  Plane,
  parseSource,
  resolveCircumstance,
  Scale,
} from "./circumstance-config";

/** Port of EligibilityConfidenceBadge.vue — detection-confidence pill. */
function ConfidenceBadge({ confidence, className }: { confidence: CxConfidence; className?: string }) {
  const normalized = confidence?.toLowerCase();
  let label: string;
  let Icon: typeof BadgeCheck;
  let iconColor: string;

  switch (normalized) {
    case "high":
    case "haute":
    case "élevée":
      label = "Confiance élevée";
      Icon = BadgeCheck;
      iconColor = "text-teal-500";
      break;
    case "medium":
    case "moyenne":
    case "modérée":
      label = "Confiance moyenne";
      Icon = AlertCircle;
      iconColor = "text-amber-500";
      break;
    case "low":
    case "faible":
    case "basse":
      label = "Confiance faible";
      Icon = MessageCircleWarning;
      iconColor = "text-rose-500";
      break;
    default:
      label = normalized || "Inconnue";
      Icon = AlertCircle;
      iconColor = "text-gray-500";
  }

  return (
    <span
      className={cn(
        "inline-flex items-center gap-x-1.5 rounded-full bg-white px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200",
        className,
      )}
    >
      <Icon className={cn("h-3.5 w-3.5", iconColor)} />
      {label}
    </span>
  );
}

// Decision-support card for one detected extraordinary circumstance (art. 5 §3 CE 261/2004).
// Variation 2 ("bandeau d'alerte"): the visual framing follows detection confidence — amber band when
// high, neutral grey otherwise. The only action is "Rejeter" (discard), surfaced via onDiscard.
interface CircumstanceCardProps {
  circumstance: ExtraordinaryCircumstance;
  /** "alert" = coloured band (default, to privilege); "compact" = sober header for narrow drawers. */
  variant?: "alert" | "compact";
  /** Narrow container (drawer): single-column sources. */
  dense?: boolean;
  /** Disable the action button while a request is in flight. */
  busy?: boolean;
  /** Footer action label (e.g. "Rejeter la détection" / "Restaurer la détection"). */
  actionText?: string;
  onDiscard: () => void;
  /** Starts header-only; the rest unfolds on click (when `expandable`). Default: always fully shown. */
  previewMode?: boolean;
  /** Whether unfolding is allowed while in preview mode — locked (no chevron, no click) when false. */
  expandable?: boolean;
  /** Replaces the default footer (AI mention + action), e.g. the jurist feedback on the detection. */
  footer?: ReactNode;
}

export function CircumstanceCard({
  circumstance,
  variant = "alert",
  busy = false,
  actionText = "Rejeter la détection",
  onDiscard,
  previewMode = false,
  expandable = true,
  footer,
}: CircumstanceCardProps) {
  const resolved = resolveCircumstance(circumstance.cxType);
  const framing = framingFor(circumstance.confidence);
  const period = formatPeriod(circumstance.startDate, circumstance.endDate);
  const sources = (circumstance.sources ?? []).map(parseSource);
  const isCompact = variant === "compact";
  const Icon = resolved.icon;

  const [expanded, setExpanded] = useState(!previewMode);
  const canToggle = previewMode && expandable;
  const showBody = !previewMode || expanded;

  const headerContent = (
    <>
      <span
        className={cn(
          "grid h-11 w-11 flex-none place-items-center rounded-[11px] border bg-white shadow-sm",
          resolved.category.tileClass,
        )}
      >
        <Icon size={22} strokeWidth={1.8} />
      </span>
      <div className="min-w-0 flex-1">
        <p className={cn("text-[10px] font-semibold uppercase tracking-wide", framing.accentFg)}>
          Circonstance extraordinaire détectée
        </p>
        <p className="text-pretty text-[15px] font-semibold leading-tight text-gray-900">
          {resolved.typeLabel}
        </p>
      </div>
      <ConfidenceBadge confidence={circumstance.confidence} className="flex-none" />
      {canToggle ? (
        <ChevronDown
          className={cn("h-4 w-4 flex-none text-gray-400 transition-transform", expanded && "rotate-180")}
        />
      ) : null}
    </>
  );

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white text-left shadow-sm">
      {/* HEADER */}
      <header
        className={cn(
          "border-b",
          isCompact ? "border-gray-100 bg-white" : cn(framing.bandBg, framing.bandBorder),
        )}
      >
        {canToggle ? (
          <button
            type="button"
            className="flex w-full items-center gap-3 px-4 py-3 text-left"
            onClick={() => setExpanded((current) => !current)}
          >
            {headerContent}
          </button>
        ) : (
          <div className="flex items-center gap-3 px-4 py-3">{headerContent}</div>
        )}
      </header>

      {showBody ? (
        <div className="p-4">
          {/* META */}
          <div className="flex flex-wrap gap-2">
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
                resolved.category.badgeClass,
              )}
            >
              <Icon size={13} strokeWidth={2} />
              {resolved.category.label}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11.5px] text-gray-600">
              <MapPin size={13} strokeWidth={1.8} />
              {resolved.scopeLabel}
            </span>
            {circumstance.iata ? (
              <span className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11.5px] tabular-nums text-gray-600">
                <Plane size={13} strokeWidth={1.8} />
                {circumstance.iata}
              </span>
            ) : null}
            <span className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11.5px] tabular-nums text-gray-600">
              <Calendar size={13} strokeWidth={1.8} />
              {period}
            </span>
          </div>

          {/* SUMMARY */}
          <p className="text-pretty mt-3.5 text-sm font-medium leading-relaxed text-gray-800">
            {circumstance.summary}
          </p>

          {/* LEGAL NOTE */}
          {resolved.category.note ? (
            <aside
              className={cn(
                "mt-3.5 flex gap-2.5 rounded-lg border p-3",
                framing.calloutBg,
                framing.calloutBorder,
              )}
            >
              <Scale size={16} strokeWidth={1.8} className={cn("mt-0.5 flex-none", framing.accentFg)} />
              <p className={cn("text-xs leading-relaxed", framing.calloutFg)}>
                <strong className={framing.accentFg}>Point de vigilance juridique — </strong>
                {resolved.category.note}
              </p>
            </aside>
          ) : null}

          {/* DESCRIPTION */}
          {circumstance.description ? (
            <p className="mt-3.5 text-xs leading-relaxed text-gray-500">{circumstance.description}</p>
          ) : null}

          {/* SOURCES */}
          {sources.length > 0 ? (
            <section className="mt-4 border-t border-gray-100 pt-3">
              <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                Sources web
              </p>
              <div className="space-y-2">
                {sources.map((source) => (
                  <a
                    key={source.url}
                    href={source.url}
                    target="_blank"
                    rel="noopener"
                    className="group block rounded-lg border border-gray-200 bg-white p-2.5"
                  >
                    <span className="block text-xs leading-snug text-blue-600 group-hover:underline">
                      <ExternalLink
                        size={13}
                        strokeWidth={1.8}
                        className="mr-1 inline-block align-text-bottom"
                      />
                      {source.title}
                    </span>
                    <span className="block text-[11px] text-gray-400">{source.domain}</span>
                  </a>
                ))}
              </div>
            </section>
          ) : null}

          {/* FOOTER */}
          {footer !== undefined ? (
            <footer className="mt-4 border-t border-gray-100 pt-3 empty:hidden">{footer}</footer>
          ) : (
            <footer className="mt-4 flex items-center justify-between gap-2.5 border-t border-gray-100 pt-3">
              <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-400">
                <Sparkles size={13} strokeWidth={1.8} /> Détecté automatiquement · IA Upclaim
              </span>
              <Button size="sm" variant="outline" disabled={busy} onClick={onDiscard}>
                {busy ? <Loader className="animate-spin" /> : null}
                {actionText}
              </Button>
            </footer>
          )}
        </div>
      ) : null}
    </div>
  );
}
